import { EnvironmentProviders, Injectable, InjectionToken, Type, makeEnvironmentProviders, signal } from '@angular/core';
import type { LocationDto } from '@/cloud/models/tree.models';

/** Selectable time range for insights. */
export interface InsightRange {
    hours: number;
    from: Date;
    to: Date;
}

/** Equipment in the scope, as returned by /api/linked-devices (capabilities decide which panels apply). */
export interface InsightLinkedDevice {
    id: string;
    name?: string;
    parentDeviceId?: string | null;
    runtimeDeviceId?: number | null;
    online?: boolean;
    capabilities?: Record<string, boolean> | null;
    state?: Record<string, unknown> | null;
}

/** What a panel is shown for. Only rooms/locations today; processors and buildings can follow. */
export interface InsightContext {
    scope: 'location';
    id: string;
    namespaceId: string;
    linkedDevices: InsightLinkedDevice[];
    /** The room itself from the live locations list (carries `usage`); absent until locations load. */
    location?: LocationDto | null;
    /** The room's control system is offline, so `location.usage` is the last reported state. */
    usageStale?: boolean;
}

/** Room tab an insight panel is shown on. */
export type InsightTab = 'usage' | 'power' | 'feedback';

export interface InsightTabDef {
    id: InsightTab;
    /** Translation key prefix: `<key>` tab label, `<key>.overview`, `<key>.open`, `<key>.empty`, `<key>.emptyDetail`. */
    labelKey: string;
    icon: string;
    /** Show the "at a glance" summary strip (live room state plus tiles the panels publish). */
    summary: boolean;
}

/** The room statistics tabs, in tab order. */
export const INSIGHT_TABS: readonly InsightTabDef[] = [
    { id: 'usage', labelKey: 'insights.usage', icon: 'pi pi-users', summary: true },
    { id: 'power', labelKey: 'insights.power', icon: 'pi pi-bolt', summary: false },
    { id: 'feedback', labelKey: 'insights.feedback', icon: 'pi pi-star', summary: true }
];

/**
 * A statistics panel for a room statistics tab (Room Usage or Power). Register one per metric type
 * with `provideInsightPanel` without touching the page.
 */
export interface InsightPanelDef {
    id: string;
    /** The room tab the panel is shown on. */
    tab: InsightTab;
    /** Translation key for the panel title. */
    labelKey: string;
    icon: string;
    order: number;
    /** Grid width: `full` (default) spans both columns on wide screens, `half` takes one. */
    span?: 'full' | 'half';
    /** Standalone component with inputs `context: InsightContext` and `range: InsightRange`. */
    component: Type<unknown>;
    /** Whether the panel has anything to show for this scope. */
    isAvailable(context: InsightContext): boolean;
}

/** "24 h" / "7 days" / "30 days" for a range length. */
export function insightRangeLabel(hours: number, t: (key: string) => string): string {
    return hours <= 24 ? t('metrics.range24h') : hours <= 24 * 7 ? t('metrics.range7d') : t('metrics.range30d');
}

export const INSIGHT_PANELS = new InjectionToken<InsightPanelDef[]>('INSIGHT_PANELS');

export function provideInsightPanel(panel: InsightPanelDef): EnvironmentProviders {
    return makeEnvironmentProviders([{ provide: INSIGHT_PANELS, useValue: panel, multi: true }]);
}

/** A KPI a panel contributes to the "at a glance" strip above the panels. */
export interface InsightSummaryTile {
    key: string;
    label: string;
    value?: number | null;
    /** Shown instead of the number (e.g. "Occupied"). */
    text?: string | null;
    unit?: string;
    icon?: string;
    caption?: string | null;
    format?: string;
}

/**
 * Collects summary tiles from the panels on one room statistics tab. Provided by the page; a panel
 * injects it (optionally) and publishes its tiles under its id, and clears them on destroy.
 */
@Injectable()
export class InsightSummaryStore {
    private readonly byPanel = signal<Record<string, InsightSummaryTile[]>>({});

    /** Tiles of the given panels, in the order given. */
    tilesFor(panelIds: string[]): InsightSummaryTile[] {
        const all = this.byPanel();
        return panelIds.flatMap((id) => all[id] ?? []);
    }

    set(panelId: string, tiles: InsightSummaryTile[]): void {
        this.byPanel.update((current) => ({ ...current, [panelId]: tiles }));
    }

    clear(panelId: string): void {
        this.byPanel.update((current) => {
            if (!(panelId in current)) return current;
            const next = { ...current };
            delete next[panelId];
            return next;
        });
    }
}
